import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Deal, createEmptyDeal, migrateDeal } from '../types/deal';
import dealsService from '../services/deals';
import authService from '../services/auth';

const LOCAL_DEALS_KEY = 'dealscout.deals.v1';
const LOCAL_MIGRATED_KEY = 'dealscout.deals.migrated.v1';
const CURRENT_KEY = 'dealscout.currentDealId';

/** idle = not yet hydrated; loading = in flight; ready/error = terminal hydrate results */
export type LoadStatus = 'idle' | 'loading' | 'ready' | 'error';
export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface DealContextValue {
  deals: Deal[];
  currentDeal: Deal | null;
  currentDealId: string | null;
  setCurrentDealId: (id: string | null) => void;
  getDeal: (id: string) => Deal | undefined;
  createDeal: (basics: Partial<Deal>) => Promise<Deal>;
  updateDeal: (id: string, patch: Partial<Deal>) => void;
  /** Non-optimistic: removes from state only after backend 204. */
  deleteDeal: (id: string) => Promise<void>;
  loadStatus: LoadStatus;
  loadError: string | null;
  saveStatus: SaveStatus;
  saveError: string | null;
  deletingDealId: string | null;
  deleteError: string | null;
  clearDeleteError: () => void;
  retrySave: () => void;
  refreshDeals: () => Promise<void>;
  pendingLocalImportCount: number;
  importLocalDeals: () => Promise<number>;
  dismissLocalImport: () => void;
}

const DealContext = createContext<DealContextValue | undefined>(undefined);

const readLocalDeals = (): Deal[] => {
  try {
    const raw = localStorage.getItem(LOCAL_DEALS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((item) => migrateDeal(item));
  } catch {
    return [];
  }
};

const apiErrorMessage = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
    ?.message ||
  (err instanceof Error ? err.message : fallback);

export const DealProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [currentDealId, setCurrentDealIdState] = useState<string | null>(
    () => localStorage.getItem(CURRENT_KEY)
  );
  const [loadStatus, setLoadStatus] = useState<LoadStatus>('idle');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deletingDealId, setDeletingDealId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [pendingLocalImportCount, setPendingLocalImportCount] = useState(0);

  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const pendingPatches = useRef<Record<string, Partial<Deal>>>({});

  const flushPatch = useCallback(async (id: string) => {
    const patch = pendingPatches.current[id];
    if (!patch || !Object.keys(patch).length) return;
    pendingPatches.current[id] = {};
    setSaveStatus('saving');
    setSaveError(null);
    try {
      const updated = await dealsService.update(id, patch);
      const migrated = migrateDeal(updated);
      setDeals((prev) => prev.map((d) => (d.id === id ? migrated : d)));
      setSaveStatus('saved');
    } catch (err: unknown) {
      setSaveStatus('error');
      setSaveError(apiErrorMessage(err, 'Save failed'));
      pendingPatches.current[id] = { ...patch, ...pendingPatches.current[id] };
    }
  }, []);

  const refreshDeals = useCallback(async () => {
    setLoadStatus('loading');
    setLoadError(null);

    if (!authService.getToken()) {
      // Terminal state: never leave Dashboard spinning on idle
      setDeals([]);
      setLoadStatus('ready');
      setPendingLocalImportCount(0);
      return;
    }

    try {
      const list = await dealsService.list();
      setDeals(list.map((d) => migrateDeal(d)));
      setLoadStatus('ready');

      const migrated = localStorage.getItem(LOCAL_MIGRATED_KEY);
      const local = readLocalDeals();
      setPendingLocalImportCount(!migrated && local.length > 0 ? local.length : 0);
    } catch (err: unknown) {
      setLoadStatus('error');
      setLoadError(apiErrorMessage(err, 'Could not load deals'));
    }
  }, []);

  useEffect(() => {
    refreshDeals();
  }, [refreshDeals]);

  useEffect(() => {
    if (currentDealId) {
      localStorage.setItem(CURRENT_KEY, currentDealId);
    } else {
      localStorage.removeItem(CURRENT_KEY);
    }
  }, [currentDealId]);

  const setCurrentDealId = useCallback((id: string | null) => {
    setCurrentDealIdState(id);
  }, []);

  const getDeal = useCallback((id: string) => deals.find((d) => d.id === id), [deals]);

  const createDeal = useCallback(async (basics: Partial<Deal>) => {
    const payload = createEmptyDeal(basics);
    const created = await dealsService.create(payload);
    const deal = migrateDeal(created);
    setDeals((prev) => [deal, ...prev]);
    setCurrentDealIdState(deal.id);
    setSaveStatus('saved');
    setLoadStatus('ready');
    setLoadError(null);
    return deal;
  }, []);

  const updateDeal = useCallback(
    (id: string, patch: Partial<Deal>) => {
      setDeals((prev) =>
        prev.map((deal) =>
          deal.id === id
            ? {
                ...deal,
                ...patch,
                id: deal.id,
                createdAt: deal.createdAt,
                updatedAt: new Date().toISOString(),
              }
            : deal
        )
      );
      pendingPatches.current[id] = { ...pendingPatches.current[id], ...patch };
      setSaveStatus('saving');
      if (debounceTimers.current[id]) clearTimeout(debounceTimers.current[id]);
      debounceTimers.current[id] = setTimeout(() => {
        flushPatch(id);
      }, 400);
    },
    [flushPatch]
  );

  const deleteDeal = useCallback(async (id: string) => {
    setDeletingDealId(id);
    setDeleteError(null);
    try {
      await dealsService.remove(id);
      setDeals((prev) => prev.filter((deal) => deal.id !== id));
      setCurrentDealIdState((current) => (current === id ? null : current));
    } catch (err: unknown) {
      setDeleteError(apiErrorMessage(err, 'Could not delete deal. Try again.'));
      throw err;
    } finally {
      setDeletingDealId(null);
    }
  }, []);

  const clearDeleteError = useCallback(() => setDeleteError(null), []);

  const retrySave = useCallback(() => {
    Object.keys(pendingPatches.current).forEach((id) => {
      flushPatch(id);
    });
  }, [flushPatch]);

  const importLocalDeals = useCallback(async () => {
    const local = readLocalDeals();
    if (!local.length) {
      setPendingLocalImportCount(0);
      return 0;
    }
    const imported = await dealsService.importLocal(local);
    localStorage.setItem(LOCAL_MIGRATED_KEY, new Date().toISOString());
    localStorage.removeItem(LOCAL_DEALS_KEY);
    setPendingLocalImportCount(0);
    await refreshDeals();
    return imported.length;
  }, [refreshDeals]);

  const dismissLocalImport = useCallback(() => {
    localStorage.setItem(LOCAL_MIGRATED_KEY, new Date().toISOString());
    setPendingLocalImportCount(0);
  }, []);

  const currentDeal = useMemo(
    () => deals.find((d) => d.id === currentDealId) || null,
    [deals, currentDealId]
  );

  const value = useMemo(
    () => ({
      deals,
      currentDeal,
      currentDealId,
      setCurrentDealId,
      getDeal,
      createDeal,
      updateDeal,
      deleteDeal,
      loadStatus,
      loadError,
      saveStatus,
      saveError,
      deletingDealId,
      deleteError,
      clearDeleteError,
      retrySave,
      refreshDeals,
      pendingLocalImportCount,
      importLocalDeals,
      dismissLocalImport,
    }),
    [
      deals,
      currentDeal,
      currentDealId,
      setCurrentDealId,
      getDeal,
      createDeal,
      updateDeal,
      deleteDeal,
      loadStatus,
      loadError,
      saveStatus,
      saveError,
      deletingDealId,
      deleteError,
      clearDeleteError,
      retrySave,
      refreshDeals,
      pendingLocalImportCount,
      importLocalDeals,
      dismissLocalImport,
    ]
  );

  return <DealContext.Provider value={value}>{children}</DealContext.Provider>;
};

export const useDeals = (): DealContextValue => {
  const ctx = useContext(DealContext);
  if (!ctx) {
    throw new Error('useDeals must be used within a DealProvider');
  }
  return ctx;
};
